import type { PriceUnitDto, PriceUnitType } from '@/api';
import { Permission } from '@/api';
import { FullSizeSpinner } from '@/components/full-size-spinner';
import BulkActionsBar from '@/components/table/bulk-actions-bar';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { useHasPermissions } from '@/hooks/use-has-permissions';
import { useNotification } from '@/hooks/use-notification';
import { connectColors } from '@/theme/colors';
import { extractDropdownValue, extractDropdownValues } from '@/utils/dropdown';
import { normalizeApiError } from '@/utils/error-utils';
import { Button, Tooltip } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { useMemo, useRef, useState } from 'react';
import {
  useCalculationSettings,
  useCalculationTypes,
  useCreateCalculationSettings,
  useUpdateCalculationSettings,
} from '../../calculation-settings-queries';
import { CreateCalculationSettingsModal } from './components/create-calculation-settings-modal';
import { FilterBar } from './components/filter-bar';
import { PriceUnitSettingsCard } from './components/price-unit-settings-card';
import type { MenuItem, PriceUnitCalculationSettings } from './types';
import {
  comparePriceUnitIds,
  getUniqueValues,
  mapCalculationSettingsToPriceUnitSettings,
  mapPriceUnitSettingsToRequest,
} from './utils';

const getSettingKey = (s: PriceUnitCalculationSettings) =>
  `${s.priceUnitId}-${s.year}`;

export function SettingsContent() {
  const canWriteCalculationSettings = useHasPermissions([
    Permission.WriteCalculationSettings,
  ]);
  const showNotification = useNotification();
  const showErrorNotification = useErrorNotification();
  const {
    data: calculationTypesResponse,
    isLoading: isCalculationTypesLoading,
    isError: isCalculationTypesError,
    refetch: refetchCalculationTypes,
  } = useCalculationTypes();
  const {
    data: calculationSettingsData,
    isLoading: isCalculationSettingsLoading,
    isError: isCalculationSettingsError,
    refetch: refetchCalculationSettings,
  } = useCalculationSettings();
  const updateCalculationSettings = useUpdateCalculationSettings();
  const createCalculationSettings = useCreateCalculationSettings();

  const editableSettings = useMemo(
    () =>
      (calculationSettingsData ?? [])
        .map(mapCalculationSettingsToPriceUnitSettings)
        .sort((a, b) => comparePriceUnitIds(a.priceUnitId, b.priceUnitId)),
    [calculationSettingsData],
  );

  const settingsByPriceUnitId = useMemo(
    () => new Map(editableSettings.map((s) => [s.priceUnitId, s])),
    [editableSettings],
  );

  const [searchValue, setSearchValue] = useState('');
  const [selectedYear, setSelectedYear] = useState(
    String(new Date().getFullYear() + 1),
  );
  const [selectedPriceUnitTypes, setSelectedPriceUnitTypes] = useState<
    PriceUnitType[]
  >([]);
  const [selectedFilterPriceUnitIds, setSelectedFilterPriceUnitIds] = useState<
    string[]
  >([]);
  const [selectedPriceUnits, setSelectedPriceUnits] = useState<Set<string>>(
    new Set(),
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const createSettingsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [draftSettings, setDraftSettings] =
    useState<PriceUnitCalculationSettings | null>(null);

  const existingPriceUnitYearKeys = useMemo(
    () => new Set(editableSettings.map(getSettingKey)),
    [editableSettings],
  );

  const availableYears = useMemo(() => {
    const years = new Set(editableSettings.map((c) => c.year));
    const nextYear = String(new Date().getFullYear() + 1);
    years.add(nextYear);
    return [...years].sort();
  }, [editableSettings]);

  const availablePriceUnitTypes = useMemo(
    () =>
      getUniqueValues(editableSettings, (settings) => settings.priceUnitType),
    [editableSettings],
  ) as PriceUnitType[];

  const filterPriceUnitOptions = useMemo(
    () =>
      editableSettings
        .filter((s) => s.year === selectedYear)
        .map((s) => ({
          id: s.priceUnitId,
          label: `${s.priceUnitId} - ${s.priceUnitName}`,
        }))
        .sort((a, b) => comparePriceUnitIds(a.id, b.id)),
    [editableSettings, selectedYear],
  );

  const handleYearChange = (value: MenuItem[]) => {
    const year = extractDropdownValue(value);
    if (year) setSelectedYear(year);
  };

  const handlePriceUnitTypeChange = (value: MenuItem[]) =>
    setSelectedPriceUnitTypes(extractDropdownValues(value) as PriceUnitType[]);

  const handleFilterPriceUnitChange = (value: MenuItem[]) =>
    setSelectedFilterPriceUnitIds(extractDropdownValues(value));

  const handleSaveSettings = (
    priceUnitSettings: PriceUnitCalculationSettings,
  ) => {
    if (!canWriteCalculationSettings) {
      return;
    }

    const calculationSettingsRequestBody =
      mapPriceUnitSettingsToRequest(priceUnitSettings);

    updateCalculationSettings.mutate(
      {
        id: priceUnitSettings.id,
        calculationSettingsRequestBody,
      },
      {
        onSuccess: () => {
          showNotification({
            type: 'success',
            title: 'Settings updated',
            description: `Calculation settings for price unit ${priceUnitSettings.priceUnitId} have been saved.`,
            duration: 5_000,
          });
        },
        onError: (err) =>
          showErrorNotification(
            normalizeApiError(err),
            'save calculation settings',
          ),
      },
    );
  };

  const handleCreateSettings = (
    priceUnitSettings: PriceUnitCalculationSettings,
  ) => {
    if (!canWriteCalculationSettings) {
      return;
    }

    const calculationSettingsRequestBody =
      mapPriceUnitSettingsToRequest(priceUnitSettings);

    createCalculationSettings.mutate(calculationSettingsRequestBody, {
      onSuccess: () => {
        setDraftSettings(null);
        showNotification({
          type: 'success',
          title: 'Settings created',
          description: `Calculation settings for price unit ${priceUnitSettings.priceUnitId} have been created. Use the filters above to find it.`,
        });
      },
      onError: (err) =>
        showErrorNotification(
          normalizeApiError(err),
          'create calculation settings',
        ),
    });
  };

  const handleCreateFromModal = (priceUnit: PriceUnitDto, year: number) => {
    setDraftSettings({
      id: 0,
      priceUnitId: String(priceUnit.id),
      priceUnitName: priceUnit.name || String(priceUnit.id),
      priceUnitType: priceUnit.type,
      year: String(year),
      calculationType: undefined,
      autoCalcEnabled: false,
      lpFmcTarget: '',
      targetLpIndex: '',
      aboveThresholdMax: '',
      aboveThresholdMin: '',
      belowThresholdMax: '',
      belowThresholdMin: '',
      threshold: '',
    });
  };

  const filteredPriceUnits = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    const year = selectedYear.trim();

    return editableSettings.filter((settings) => {
      if (settings.year !== year) return false;
      if (
        selectedPriceUnitTypes.length > 0 &&
        !selectedPriceUnitTypes.includes(settings.priceUnitType)
      )
        return false;
      if (
        selectedFilterPriceUnitIds.length > 0 &&
        !selectedFilterPriceUnitIds.includes(settings.priceUnitId)
      )
        return false;
      if (search === '') return true;
      return (
        settings.priceUnitId.toLowerCase().includes(search) ||
        settings.priceUnitName.toLowerCase().includes(search)
      );
    });
  }, [
    searchValue,
    selectedYear,
    selectedPriceUnitTypes,
    selectedFilterPriceUnitIds,
    editableSettings,
  ]);

  const selectAll = useMemo(() => {
    if (filteredPriceUnits.length === 0) return false;
    return filteredPriceUnits.every((s) =>
      selectedPriceUnits.has(s.priceUnitId),
    );
  }, [filteredPriceUnits, selectedPriceUnits]);

  const handleSelectAllChange = (checked: boolean) => {
    const newSelected = new Set(selectedPriceUnits);
    for (const settings of filteredPriceUnits) {
      if (checked) newSelected.add(settings.priceUnitId);
      else newSelected.delete(settings.priceUnitId);
    }
    setSelectedPriceUnits(newSelected);
  };

  const handlePriceUnitSelect = (priceUnitId: string, checked: boolean) => {
    const newSelected = new Set(selectedPriceUnits);
    if (checked) newSelected.add(priceUnitId);
    else newSelected.delete(priceUnitId);
    setSelectedPriceUnits(newSelected);
  };

  if (isCalculationTypesLoading || isCalculationSettingsLoading) {
    return <FullSizeSpinner type="full-container" />;
  }

  if (isCalculationTypesError || isCalculationSettingsError) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap={4}
        padding={10}
      >
        <Text fontSize="lg" color={connectColors.core.gray}>
          Failed to load calculation settings. Please try again.
        </Text>
        <Button
          variant="primary"
          appearance="solid"
          size="medium"
          onClick={() => {
            if (isCalculationTypesError) refetchCalculationTypes();
            if (isCalculationSettingsError) refetchCalculationSettings();
          }}
        >
          Retry
        </Button>
      </Flex>
    );
  }

  if (!calculationTypesResponse) {
    return null;
  }

  const { calculationTypes, rmCalculationTypes, rrpCalculationTypes } =
    calculationTypesResponse;

  return (
    <Flex flexDirection="column" gap={4} padding={8} height="100%">
      <Flex justifyContent="space-between" alignItems="center">
        {selectedPriceUnits.size > 0 ? (
          <BulkActionsBar
            selectedItemsCount={selectedPriceUnits.size}
            onDeselectAll={() => setSelectedPriceUnits(new Set())}
            position="relative"
            width="100%"
            actions={
              <Flex gap={'16px'} align={'center'}>
                <Button
                  size="small"
                  iconName="pen"
                  disabled
                  aria-label="Edit selected"
                  style={{ backgroundColor: 'transparent', padding: 0 }}
                >
                  Edit (coming soon)
                </Button>
              </Flex>
            }
          />
        ) : (
          <>
            <FilterBar
              selectAll={selectAll}
              onSelectAllChange={handleSelectAllChange}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              selectedYear={selectedYear}
              availableYears={availableYears}
              selectedPriceUnitTypes={selectedPriceUnitTypes}
              availablePriceUnitTypes={availablePriceUnitTypes}
              selectedFilterPriceUnitIds={selectedFilterPriceUnitIds}
              availablePriceUnits={filterPriceUnitOptions}
              onYearChange={handleYearChange}
              onPriceUnitTypeChange={handlePriceUnitTypeChange}
              onFilterPriceUnitChange={handleFilterPriceUnitChange}
            />
            {canWriteCalculationSettings && (
              <Tooltip isHidden={!draftSettings} position="bottom">
                <Tooltip.Trigger>
                  <span>
                    <Button
                      appearance="solid"
                      variant="primary"
                      size="medium"
                      iconName="control-plus"
                      ref={createSettingsTriggerRef}
                      disabled={!!draftSettings}
                      onClick={() => setIsCreateModalOpen(true)}
                    >
                      Add new
                    </Button>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  A new setting is already being created
                </Tooltip.Content>
              </Tooltip>
            )}
          </>
        )}
      </Flex>

      <Flex flexDirection="column" gap={6} padding={0}>
        {draftSettings && (
          <PriceUnitSettingsCard
            key="draft"
            mode="create"
            canEdit={canWriteCalculationSettings}
            priceUnitSettings={draftSettings}
            editableSettings={editableSettings}
            settingsByPriceUnitId={settingsByPriceUnitId}
            isSaving={createCalculationSettings.isPending}
            onSave={handleCreateSettings}
            onCancel={() => setDraftSettings(null)}
            calculationTypes={calculationTypes}
            rmCalculationTypes={rmCalculationTypes}
            rrpCalculationTypes={rrpCalculationTypes}
          />
        )}
        {filteredPriceUnits.map((priceUnitSettings) => (
          <PriceUnitSettingsCard
            key={getSettingKey(priceUnitSettings)}
            canEdit={canWriteCalculationSettings}
            priceUnitSettings={priceUnitSettings}
            editableSettings={editableSettings}
            settingsByPriceUnitId={settingsByPriceUnitId}
            isSelected={selectedPriceUnits.has(priceUnitSettings.priceUnitId)}
            isSaving={updateCalculationSettings.isPending}
            onSelect={handlePriceUnitSelect}
            onSave={handleSaveSettings}
            calculationTypes={calculationTypes}
            rmCalculationTypes={rmCalculationTypes}
            rrpCalculationTypes={rrpCalculationTypes}
          />
        ))}
        {filteredPriceUnits.length === 0 && !draftSettings && (
          <Flex
            justifyContent="center"
            alignItems="center"
            padding={8}
            backgroundColor={connectColors.core.white}
            borderRadius={8}
          >
            <Text fontSize="lg" color={connectColors.core.gray}>
              No price units found matching your filters
            </Text>
          </Flex>
        )}
      </Flex>

      <CreateCalculationSettingsModal
        isOpen={isCreateModalOpen}
        triggerRef={createSettingsTriggerRef}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateFromModal}
        existingPriceUnitYearKeys={existingPriceUnitYearKeys}
      />
    </Flex>
  );
}
